import { type SyntheticEvent, useEffect, useState } from 'react';

import {
  Box,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import { XIcon } from 'lucide-react';

import { SlideDialog } from '@modules/_shared/components/SlideDialog';
import { useLocale } from '@shared/hooks/locale';

export type BuyerLeadLostReasonOption = {
  id: string;
  label?: string | null;
};

type BuyerLeadLostReasonDialogProps = {
  open: boolean;
  stages: BuyerLeadLostReasonOption[];
  onClose: () => void;
  onConfirm: (stageId: string) => Promise<void>;
};

const stopPropagation = (event: SyntheticEvent) => event.stopPropagation();

export const BuyerLeadLostReasonDialog = ({
  open,
  stages,
  onClose,
  onConfirm,
}: BuyerLeadLostReasonDialogProps) => {
  const { t } = useLocale();
  const [selectedStageId, setSelectedStageId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setSelectedStageId(stages[0]?.id ?? null);
    }
  }, [open, stages]);

  const handleConfirm = async () => {
    if (selectedStageId == null) {
      return;
    }

    setSaving(true);
    try {
      await onConfirm(selectedStageId);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box
      sx={{ display: 'contents' }}
      onClick={stopPropagation}
      onPointerDown={stopPropagation}
      onKeyDown={stopPropagation}
    >
      <SlideDialog
        open={open}
        onClose={onClose}
        title={t('Lead lost')}
        onSave={handleConfirm}
        loading={saving}
        saveDisabled={selectedStageId == null || saving}
        saveText={t('Mark as lost')}
        cancelText={t('Cancel')}
        saveColor="error"
        saveIcon={<XIcon />}
      >
        <Stack sx={{ gap: 2, p: 2 }}>
          <Typography variant="body1">{t('Why is this lead dead?')}</Typography>
          <FormControl fullWidth>
            <RadioGroup
              value={selectedStageId ?? ''}
              onChange={event => setSelectedStageId(event.target.value)}
            >
              {stages.map(stage => (
                <FormControlLabel
                  key={stage.id}
                  value={stage.id}
                  control={<Radio />}
                  label={stage.label ?? ''}
                />
              ))}
            </RadioGroup>
          </FormControl>
        </Stack>
      </SlideDialog>
    </Box>
  );
};
